import { Injectable, signal } from "@angular/core";
import { db, Media } from "../../../core/database/db";
import { LoggerService } from "../../../shared/services/logger.service";
import SyncStatusService, { SyncStatus } from "../sync-status-service";
import { NetworkStatusService } from "../../../shared/services/network-status.service";
import { base64ToBlob, blobToBase64, BlobService } from "../../../shared/services/blob.service";
import { UploadApiService, UploadTicket } from "./upload-api.service";
import { toObservable } from "@angular/core/rxjs-interop";

const BLOCK_SIZE = 512 * 1024;   // 512 KiB
const CONCURRENCY = 3;            // parallel HTTP PUTs
const THUMBNAIL_WIDTH_PX = 150;

const getFormatedFile = (rec: Media | null) => `${rec?.name}, id = ${rec?.id}, fileIdentifier= ${rec?.fileIdentifier}, size = (${rec?.size} bytes)`;

export interface ProgressRow {
  id: number;
  pct: number;
  status: "queued" | "uploading" | "done" | "error";
  errMsg?: string;
}

@Injectable({ providedIn: "root" })
export class UploadManagerService {

  private _progressRows = signal<ProgressRow[]>([]);
  private isProcessing = false;
  private isOnline: boolean = true;

  // Public, read-only rows signal used to display or calculate progress by file
  readonly progressRows = this._progressRows.asReadonly();

  constructor(
    private logger: LoggerService,
    private syncStatusService: SyncStatusService,
    private networkStatusService: NetworkStatusService,
    private uploadService: UploadApiService,
    private blobService: BlobService
  ) {
    this.networkStatusService.online$.subscribe(isOnline => this.isOnline = isOnline);

    toObservable(this.progressRows).subscribe(rs => {
      const totalFiles = rs.length;
      const completedFiles = rs.filter(r => r.status === "done").length;
      const erroredFiles = rs.filter(r => r.status === "error").length;

      if (totalFiles) {
        this.logger.info(`[UploadManager] progress update: ${completedFiles} of ${totalFiles} files uploaded, ${erroredFiles} errors`);
      }
    });
  }

  public async markFilesReadyForUpload(mediaIds: number[]): Promise<void> {
    this.logger.event("UploadManager:MarkFilesReadyForUpload", { mediaIds });

    await db.transaction("rw", [db.media, db.syncStatus], async () => {
      try {
        await db.media.bulkUpdate(mediaIds.map(mediaId => ({ key: mediaId, changes: { readyForUpload: 1 } })));
      } catch (error) {
        if (error && (error as Error)?.message?.includes("ConstraintError")) {
          this.logger.error("[UploadManager] markFilesReadyForUpload: ConstraintError encountered", error);
        } else {
          throw error;
        }
      }
    });

    this._progressRows.update(rs => [
      ...rs,
      ...mediaIds.map(id => {
        if (this.isOnline) {
          this.syncStatusService.addSyncProgressItem(`Upload:${id}`);
        }
        return { id, pct: 0, status: "queued" as const };
      })
    ]);

    this.startUpload();
  }

  public async startUpload(isForceSync = false): Promise<void> {
    this.logger.info(`[UploadManager:StartUpload] Start image upload called with isForceSync=${isForceSync}`);

    if (isForceSync === false && this.isProcessing) {
      this.logger.info("[UploadManager:StartUpload] Upload already in progress, skipping");
      return;
    }
    let itemToUpload: Media | null = null;

    try {
      if (!this.isOnline) {
        this.logger.info("[UploadManager] offline, skipping upload");
        this.isProcessing = false;
        return; // respect the externally supplied flag
      }

      // Skip if offline
      if (typeof self.navigator !== "undefined" && !self.navigator.onLine) {
        this.logger.info("[UploadManager] offline, skipping upload");
        this.isProcessing = false;
        return;
      }

      this.isProcessing = true;
      itemToUpload = await this.getNextPendingUpload(isForceSync);
      if (!itemToUpload) {
        this.isProcessing = false;
        return;
      }

      const { sasUri } = await this.getUploadTicket(itemToUpload);
      await this.uploadToAzureBlobStorage(itemToUpload, sasUri);
      await this.confirmUploadToServer(itemToUpload);
      await this.confirmUploadToLocalDb(itemToUpload);
      this.onUploadComplete(itemToUpload);
      this.syncStatusService.markSyncProgressItemComplete(`Upload:${itemToUpload.id}`);

      this.isProcessing = false;
      await this.checkForMoreUploadsNeeded();
    } catch (err) {
      this.logger.error(`[UploadManager] upload failed for record: ${getFormatedFile(itemToUpload)}`);
      this.logger.error(err);
      this.onUploadError(itemToUpload, err as Error);
      this.isProcessing = false;
    }
  }

  public areUploadsInProgress(): boolean {
    return this.syncStatusService.containsSyncProgressItem("Upload:");
  }

  private onUploadProgress(rec: Media, pct: number) {
    this._progressRows.update(rs =>
      rs.map(r =>
        (r.id === rec.id ? { ...r, pct, status: "uploading" } : r)
      ));
  }

  private onUploadComplete(rec: Media) {
    this.logger.event("UploadManager:UploadComplete", { message: `Upload complete for: ${getFormatedFile(rec)}` });

    this._progressRows.update(rs =>
      rs.map(r =>
        (r.id === rec.id ? { ...r, pct: 100, status: "done" } : r)
      ));
  }

  private onUploadError(rec: Media | null, error: Error) {
    this.logger.event("UploadManager:UploadError", { message: `Upload error for file: ${getFormatedFile(rec)}`, error });

    this._progressRows.update(rs =>
      rs.map(r =>
        (r.id === rec?.id ? { ...r, status: "error", errMsg: error.message } : r)
      ));
  }

  private makeBlockId(n: number) {
    return btoa(String(n).padStart(8, "0")); // 00000000 ⇒ base64
  }

  private async putBlock(uri: string, id: string, body: Blob) {
    const response = await fetch(
      `${uri}&comp=block&blockid=${encodeURIComponent(id)}`,
      {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob" },
        body
      }
    );

    if (!response.ok) {
      throw new Error(`putBlock failed: ${response.status} ${response.statusText}`);
    }

    return response;
  };

  private async putBlockList(uri: string, ids: string[]) {
    const response = await fetch(`${uri}&comp=blocklist`, {
      method: "PUT",
      headers: { "Content-Type": "application/xml" },
      body: `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids.map(i =>
        `<Latest>${i}</Latest>`).join("")
        }</BlockList>`
    });

    if (!response.ok) {
      throw new Error(`putBlockList failed: ${response.status} ${response.statusText}`);
    }

    return response;
  };

  private async getNextPendingUpload(isForceSync = false) {
    this.logger.info("[UploadManager:NextPendingUpload] Searching for next pending upload");

    const record = await db.media.where({
      readyForUpload: isForceSync ? [0, 1] : 1,
      uploaded: 0
    }).first();

    if (record) {
      this.logger.event("UploadManager:NextPendingUpload", { message: `Found pending upload: ${getFormatedFile(record)}` });
      this.syncStatusService.addSyncProgressItem(`Upload:${record.id}`);
      this.syncStatusService.setSyncStatus(SyncStatus.InProgress);
      return record;
    }

    this.logger.info("[UploadManager:NextPendingUpload] No pending uploads found");
    this._progressRows.set([]);
    if (this.syncStatusService.getSyncStatus() === SyncStatus.InProgress) {
      this.syncStatusService.setSyncStatus(SyncStatus.Complete);
    }
    return null;
  }

  private async getUploadTicket(rec: Media): Promise<UploadTicket> {
    const { name, size, fileIdentifier: fileGuid, imageType } = rec;

    if (!!name === false) {
      this.logger.error(`[UploadManager] confirmUpload called with invalid record (missing name): ${getFormatedFile(rec)}`);
      throw new Error("invalid record, missing name");
    }

    if (!!size === false || size <= 0) {
      this.logger.error(`[UploadManager] getTicket called with invalid record (missing or zero size): ${getFormatedFile(rec)}`);
      throw new Error("invalid record, missing or zero size");
    }

    this.logger.event("UploadManager:GetUploadTicket", { message: `Requesting upload ticket for: ${getFormatedFile(rec)}` });
    const ticket = await this.uploadService.getTicket(name, size, fileGuid, imageType);
    this.logger.event("UploadManager:GetUploadTicket", { message: `Ticket received for: ${getFormatedFile(rec)} with uri ${ticket.sasUri}` });

    return ticket;
  }

  private async uploadToAzureBlobStorage(rec: Media, sasUri: string): Promise<void> {
    this.logger.event("UploadManager:UploadToAzureBlobStorage", { message: `Starting upload process for: ${getFormatedFile(rec)}` });

    if (!!rec.size === false) {
      this.logger.error(`[UploadManager] zero-byte file upload for: ${getFormatedFile(rec)}`);
      return;
    }

    if (!!rec.base64Image === false) {
      this.logger.event("UploadManager:UploadToAzureBlobStorage", { message: `No base64 image found for: ${getFormatedFile(rec)}` });
      throw new Error("no base64 image data found");
    }

    /* ── stream the file in blocks ── */
    const totalSize = rec.size;
    const numberOfUploadBlocks = Math.ceil(totalSize / BLOCK_SIZE);
    let next = 0, done = 0;
    const blockIds = Array.from({ length: numberOfUploadBlocks }, (_, i) => this.makeBlockId(i));

    const blob = base64ToBlob(rec.base64Image);

    const uploadPart = async () => {
      while (next < numberOfUploadBlocks) {
        const idx = next++;
        const start = idx * BLOCK_SIZE;
        const end = Math.min(start + BLOCK_SIZE, totalSize);
        const chunk = blob.slice(start, end);
        await this.putBlock(sasUri, blockIds[idx], chunk);
        done++;

        this.logger.event("UploadManager:UploadToAzureBlobStorage", { message: `Uploaded block ${idx + 1} of ${numberOfUploadBlocks} for: ${getFormatedFile(rec)}` });
        this.onUploadProgress(rec, Math.round(100 * done / numberOfUploadBlocks));
      }
    };

    await Promise.all(Array.from({ length: CONCURRENCY }, uploadPart));
    await this.putBlockList(sasUri, blockIds);        // blob assembled in Azure
  }

  private async confirmUploadToServer(rec: Media): Promise<void> {
    const { fileIdentifier, name, imageType } = rec;

    if (!!name === false) {
      this.logger.error(`[UploadManager] confirmUpload called with invalid record (missing name): ${getFormatedFile(rec)}`);
      throw new Error("invalid record, missing name");
    }

    this.logger.event("UploadManager:ConfirmUpload", { message: `Confirming upload for: ${getFormatedFile(rec)}` });
    await this.uploadService.confirmUpload(name, fileIdentifier, imageType);
  }

  private async createThumbnailBase64(rec: Media): Promise<string | null> {
    if (!rec.base64Image || rec.isThumbnailOnly) {
      return null;
    }

    try {
      const blob = base64ToBlob(rec.base64Image, rec.mimeType ?? "image/jpeg");
      const resized = await this.blobService.resizeBlob(blob, THUMBNAIL_WIDTH_PX);
      return await blobToBase64(resized, this.logger);
    } catch (error) {
      this.logger.error("[UploadManager] failed to shrink image after upload", error);
      return null;
    }
  }

  private async confirmUploadToLocalDb(rec: Media) {
    this.logger.event("UploadManager:MarkUploaded", { message: `Marking record as uploaded: ${getFormatedFile(rec)}` });

    if (!rec.id) {
      this.logger.error("[UploadManager] markUploaded called with invalid record (missing id)");
      throw new Error("invalid record, missing id");
    }

    const thumbnailBase64 = await this.createThumbnailBase64(rec);
    const changes: Partial<Media> = {
      uploaded: 1,
      readyForUpload: 0
    };
    if (thumbnailBase64) {
      changes.base64Image = thumbnailBase64;
      changes.isThumbnailOnly = true;
    }

    return db.transaction("rw", [db.media, db.syncStatus], async () => {
      db.setSuppressHooks(true);

      // We don't need to set the sync status to "pending" because the upload confirmation sets the `isUploaded` flag
      // in the backend, thus that doesn't need to be synced back.
      await db.media.update(rec.id!, changes);

      db.setSuppressHooks(false);
    });
  }

  private async checkForMoreUploadsNeeded() {
    this.logger.event("UploadManager:checkForMoreUploadsNeeded", { message: "Checking for more uploads, starting the upload process again" });
    await this.startUpload();
  }
}
